import { MAX_DESCRIPTION_LENGTH } from './item';
import { isReservedEvent, isReservedObjectAttribute, isReservedProfileAttribute } from './reserved-descriptions';
import type { DataIndexItemKind } from './types';

/**
 * The rules a row is edited under: how long a description may be, and which
 * rows the workspace may write at all.
 */

/**
 * Why a description cannot be saved, or nothing when it can. The message
 * says "less than", which is a character out from the limit it enforces —
 * the copy is the page this replaces, and changing it is a copy decision
 * rather than a porting one.
 */
export function descriptionError(description: string): string | undefined {
  return description.length > MAX_DESCRIPTION_LENGTH
    ? `Description must be less than ${MAX_DESCRIPTION_LENGTH} characters`
    : undefined;
}

/** How much of the limit is used, shown while there is still room. */
export function descriptionCounter(description: string): string {
  return `${description.length}/${MAX_DESCRIPTION_LENGTH}`;
}

/**
 * Whether a row's description and tags are the workspace's to write.
 *
 * Customer.io owns the meaning of a reserved name, and says so in copy the
 * workspace cannot change, so those rows have nothing to edit. Which names
 * are reserved depends on the kind: `email` means something on a person and
 * nothing in particular on an object.
 *
 * Not the same question as whether a row can be selected: a bulk action also
 * refuses an attribute the workspace has promoted to an identifier, which is
 * about deleting it rather than about describing it.
 */
export function isEditableByWorkspace(kind: DataIndexItemKind, name: string): boolean {
  switch (kind) {
    case 'event':
      return !isReservedEvent(name);
    case 'object-attribute':
    case 'object-relationship-attribute':
      return !isReservedObjectAttribute(name);
    case 'attribute':
      return !isReservedProfileAttribute(name);
    default: {
      const _exhaustive: never = kind;
      throw new Error(`unhandled data index item kind: ${String(_exhaustive)}`);
    }
  }
}
